import { normalizeCityName } from "./cityCenters";

export interface SavedMapView {
  center: { lat: number; lng: number };
  zoom: number;
  neighborhood: string | null;
  city?: string | null;
}

const MAP_VIEW_STORAGE_KEY = "propertiesList_mapView";
const RETURN_CONTEXT_STORAGE_KEY = "propertiesList_returnContext";

interface ReturnContextPayload {
  view: SavedMapView;
  shouldRestore: boolean;
}

export function saveMapView(view: SavedMapView): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify(view));
  } catch {
    // sessionStorage may be unavailable in restricted contexts
  }
}

export function loadMapView(): SavedMapView | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(MAP_VIEW_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as SavedMapView;
    if (
      !parsed?.center ||
      typeof parsed.center.lat !== "number" ||
      typeof parsed.center.lng !== "number" ||
      typeof parsed.zoom !== "number"
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function saveReturnContext(view: SavedMapView): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const payload: ReturnContextPayload = { view, shouldRestore: true };
    sessionStorage.setItem(RETURN_CONTEXT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable in restricted contexts
  }
}

export function consumeReturnContext(): SavedMapView | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(RETURN_CONTEXT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    sessionStorage.removeItem(RETURN_CONTEXT_STORAGE_KEY);
    const parsed = JSON.parse(raw) as ReturnContextPayload;

    if (!parsed?.shouldRestore || !parsed.view?.center) {
      return null;
    }

    return parsed.view;
  } catch {
    return null;
  }
}

export function isMapViewCompatibleWithCity(
  view: SavedMapView | null | undefined,
  city?: string | null
): boolean {
  if (!view) {
    return false;
  }

  if (!city?.trim()) {
    return true;
  }

  if (!view.city?.trim()) {
    return normalizeCityName(city) === "fortaleza";
  }

  return normalizeCityName(view.city) === normalizeCityName(city);
}

export function filterPropertiesByNeighborhood<
  T extends { address?: { neighborhood?: string } }
>(properties: T[], neighborhood: string | null | undefined): T[] {
  if (!neighborhood) {
    return properties;
  }

  const normalizedNeighborhood = neighborhood.toLowerCase();

  return properties.filter(
    (property) =>
      property.address?.neighborhood?.toLowerCase() === normalizedNeighborhood
  );
}
